import React, { useCallback, useContext, useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import {
	ChevronLeft,
	Plus,
	ChevronDown,
	ChevronUp,
	AlertTriangle,
	TrendingUp,
	ArrowRight,
} from 'lucide-react'
import { toast } from 'react-toastify'
import { BASE_URL_API } from '../../References/URLs'
import ActiveStoreContext from '../../store/ActiveStoreContext'
import AuthContext from '../../store/auth-context'
import RejectAdvanceModal from './RejectAdvanceModal'
import styles from './StaffAdvanceLedgerScreen.module.css'

const parseActiveStore = (rawStore) => {
	try {
		return rawStore && rawStore.trim() !== '' ? JSON.parse(rawStore) : null
	} catch {
		return null
	}
}

const formatINR = (amount) => {
	const num = parseFloat(amount)
	if (!isFinite(num)) return '—'
	return num.toLocaleString('en-IN', {
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
	})
}

const STATUS_CONFIG = {
	PENDING_APPROVAL: { label: 'Pending Approval', color: '#E65100', bg: '#FFF3E0' },
	ACTIVE: { label: 'Active', color: '#2E7D32', bg: '#E8F5E9' },
	CLOSED: { label: 'Closed', color: '#546E7A', bg: '#ECEFF1' },
	WRITTEN_OFF: { label: 'Written Off', color: '#6A1B9A', bg: '#F3E5F5' },
	FORECLOSED: { label: 'Foreclosed', color: '#1565C0', bg: '#E3F2FD' },
	REJECTED: { label: 'Rejected', color: '#C62828', bg: '#FFEBEE' },
}

const EMI_STATUS_CONFIG = {
	PENDING: { icon: '◌', color: '#90A4AE' },
	DEDUCTED: { icon: '✓', color: '#2E7D32' },
	PARTIAL: { icon: '◐', color: '#E65100' },
	SKIPPED: { icon: '—', color: '#BDBDBD' },
	WAIVED: { icon: '✕', color: '#BDBDBD' },
}

const StaffAdvanceLedgerScreen = () => {
	const navigate = useNavigate()
	const location = useLocation()
	const { staffIdentifier } = useParams()
	const authCtx = useContext(AuthContext)
	const asCtx = useContext(ActiveStoreContext)
	const activeStore = parseActiveStore(asCtx?.activeStore)
	const token = authCtx?.token

	const staffNameFromState = location?.state?.staffName || ''

	const [ledgerData, setLedgerData] = useState(null)
	const [isLoading, setIsLoading] = useState(true)
	const [expandedAdvance, setExpandedAdvance] = useState(null)
	const [rejectModalOpen, setRejectModalOpen] = useState(false)
	const [selectedAdvance, setSelectedAdvance] = useState(null)

	const showToast = (message, type = 'info') => {
		toast[type](message, { position: 'bottom-center' })
	}

	const fetchAdvanceLedger = useCallback(async () => {
		if (!token || !activeStore?.storeIdentifier || !staffIdentifier) return

		try {
			setIsLoading(true)

			const response = await fetch(BASE_URL_API + 'getStaffAdvanceLedger', {
				method: 'POST',
				body: JSON.stringify({
					token: token,
					storeIdentifier: activeStore.storeIdentifier,
					staffIdentifier: staffIdentifier,
				}),
			})

			const respo = await response.json()

			if (respo?.status === 'success') {
				setLedgerData(respo.response)
			} else {
				if (respo?.errorCode === 'tokenExpired') {
					authCtx.logout()
					return
				}
				if (respo?.errorCode === 'VAL_007') {
					showToast(respo?.message || 'Staff not found.', 'error')
					navigate(-1)
					return
				}
				showToast(respo?.message || 'Failed to load advance ledger.', 'error')
				navigate(-1)
			}
		} catch (error) {
			showToast('Network error.', 'error')
		} finally {
			setIsLoading(false)
		}
	}, [token, activeStore?.storeIdentifier, staffIdentifier, authCtx, navigate])

	useEffect(() => {
		fetchAdvanceLedger()
	}, [staffIdentifier, fetchAdvanceLedger])

	if (!staffIdentifier) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<button className={styles.backBtn} onClick={() => navigate('/payroll/advance-book')}>
						<ChevronLeft size={20} />
					</button>
					<h1 className={styles.title}>No staff selected</h1>
					<div style={{ width: 40 }} />
				</div>
				<div className={styles.emptyState}>
					<AlertTriangle size={48} color="#90A4AE" />
					<p className={styles.emptyStateText}>Open this screen from Advance Book.</p>
					<button className={styles.emptyActionBtn} onClick={() => navigate('/payroll/advance-book')}>
						Go to Advance Book <ArrowRight size={16} />
					</button>
				</div>
			</div>
		)
	}

	const handleRefreshAfterReject = () => {
		setRejectModalOpen(false)
		setSelectedAdvance(null)
		fetchAdvanceLedger()
	}

	const resolvedStaffName = ledgerData?.staffName || staffNameFromState || 'Advances'

	if (isLoading) {
		return (
			<div className={styles.container}>
				<div className={styles.header}>
					<button className={styles.backBtn} onClick={() => navigate(-1)}>
						<ChevronLeft size={20} />
					</button>
					<h1 className={styles.title}>{resolvedStaffName}</h1>
					<div style={{ width: 40 }} />
				</div>
				<div className={styles.loaderContainer}>
					<div className={styles.spinner} />
					<p className={styles.loaderText}>Loading advances...</p>
				</div>
			</div>
		)
	}

	const totalAdvanceCount = ledgerData?.totalAdvanceCount || 0
	const advances = ledgerData?.advances || []

	return (
		<div className={styles.container}>
			{/* Header */}
			<div className={styles.header}>
				<button className={styles.backBtn} onClick={() => navigate(-1)}>
					<ChevronLeft size={20} />
				</button>
				<h1 className={styles.title}>{resolvedStaffName}</h1>
				<div className={styles.headerActions}>
					<button
						type="button"
						className={styles.fnfBtn}
						onClick={() =>
							navigate(`/payroll/fnf/${staffIdentifier}`, {
								state: {
									staffIdentifier,
									staffName: resolvedStaffName,
								},
							})
						}
					>
						Generate FnF <ArrowRight size={13} />
					</button>
					<button
						type="button"
						className={styles.fnfHistoryBtn}
						onClick={() =>
							navigate(`/payroll/fnf/${staffIdentifier}`, {
								state: {
									staffIdentifier,
									staffName: resolvedStaffName,
									activeTab: 'HISTORY',
								},
							})
						}
					>
						FnF History
					</button>
					<button
						className={styles.newAdvanceBtn}
						onClick={() =>
							navigate(`/payroll/advance/${staffIdentifier}/create`, {
								state: { staffName: resolvedStaffName },
							})
						}
					>
						<Plus size={18} />
					</button>
				</div>
			</div>

			{/* Content */}
			<div className={styles.content}>
				{/* Summary Strip */}
				{totalAdvanceCount > 0 && (
					<div className={styles.summaryStrip}>
						<div className={styles.summaryItem}>
							<div className={styles.summaryLabel}>Outstanding</div>
							<div className={styles.summaryValue}>
								₹{formatINR(ledgerData?.totalOutstandingAllAdvances || 0)}
							</div>
						</div>
						{ledgerData?.nextMonthTotalEMI > 0 && (
							<div className={styles.summaryItem}>
								<div className={styles.summaryLabel}>
									Next EMI ({ledgerData?.nextEMICycle})
								</div>
								<div className={styles.summaryValue}>
									₹{formatINR(ledgerData?.nextMonthTotalEMI || 0)}
								</div>
							</div>
						)}
					</div>
				)}

				{/* Empty State */}
				{totalAdvanceCount === 0 && (
					<div className={styles.emptyState}>
						<AlertTriangle size={48} color="#90A4AE" />
						<p className={styles.emptyStateText}>
							No advance records for {resolvedStaffName}.
						</p>
						<button
							className={styles.emptyActionBtn}
							onClick={() =>
								navigate(`/payroll/advance/${staffIdentifier}/create`, {
									state: { staffName: resolvedStaffName },
								})
							}
						>
							Request Advance <ArrowRight size={16} />
						</button>
					</div>
				)}

				{/* Advance Cards */}
				<div className={styles.advanceList}>
					{advances.map((advance, idx) => (
						<AdvanceCard
							key={advance.advanceIdentifier || idx}
							advance={advance}
							isExpanded={expandedAdvance === advance.advanceIdentifier}
							onToggleExpand={() =>
								setExpandedAdvance(
									expandedAdvance === advance.advanceIdentifier
										? null
										: advance.advanceIdentifier
								)
							}
							onReject={(adv) => {
								setSelectedAdvance(adv)
								setRejectModalOpen(true)
							}}
							onApprove={(adv) =>
								navigate(`/payroll/advance/${adv.advanceIdentifier}/approve`, {
									state: {
										advance: adv,
										staffName: resolvedStaffName,
									},
								})
							}
							navigate={navigate}
						/>
					))}
				</div>
			</div>

			{/* Reject Modal */}
			<RejectAdvanceModal
				isOpen={rejectModalOpen}
				advance={selectedAdvance}
				onClose={() => {
					setRejectModalOpen(false)
					setSelectedAdvance(null)
				}}
				onSuccess={handleRefreshAfterReject}
			/>
		</div>
	)
}

/* ─────────────────────────────────────────────────────────────
   AdvanceCard Sub-Component
   ───────────────────────────────────────────────────────────── */
const AdvanceCard = ({
	advance,
	isExpanded,
	onToggleExpand,
	onReject,
	onApprove,
	navigate,
}) => {
	const config = STATUS_CONFIG[advance.advanceStatus] || {
		label: advance.advanceStatus,
		color: '#424242',
		bg: '#EEEEEE',
	}

	const progressPercent =
		advance.disbursedAmount > 0
			? Math.round((advance.totalRepaid / advance.disbursedAmount) * 100)
			: 0

	const repaymentLabel =
		advance.repaymentType === 'PERCENT_OF_NET'
			? `${advance.emiPercent || 0}% of net • ${advance.paidEmiCount || 0} paid`
			: `₹${formatINR(advance.emiAmount || 0)}/month • ${
					advance.paidEmiCount || 0
				}/${advance.totalEmiCount || 0} paid`

	return (
		<div className={styles.advanceCard}>
			{/* Header */}
			<div className={styles.cardHeader}>
				<div className={styles.cardHeaderLeft}>
					<div
						className={styles.statusBadge}
						style={{ color: config.color, backgroundColor: config.bg }}
					>
						{config.label}
					</div>
					<div className={styles.cardAmount}>
						₹{formatINR(advance.approvedAmount || advance.requestedAmount || 0)}
					</div>
				</div>
			</div>

			{/* Disbursement & Balance */}
			<div className={styles.cardRow}>
				<span className={styles.cardRowLabel}>
					Disbursed: {advance.disbursedOn || '—'}
				</span>
				<span className={styles.cardRowValue}>
					₹{formatINR(advance.outstandingBalance || 0)} remaining
				</span>
			</div>

			{/* Repayment Type */}
			<div className={styles.cardRow}>
				<span className={styles.cardRowLabel}>{advance.repaymentType}</span>
				<span className={styles.cardRowValue}>{repaymentLabel}</span>
			</div>

			{/* Request Reason */}
			{advance.requestRemarks && (
				<div className={styles.cardRow}>
					<span className={styles.cardRowLabel}>Reason</span>
					<span className={styles.cardRowValue}>{advance.requestRemarks}</span>
				</div>
			)}

			{/* Closed Status */}
			{['CLOSED', 'WRITTEN_OFF', 'FORECLOSED', 'REJECTED'].includes(
				advance.advanceStatus
			) && (
				<div className={styles.cardRow}>
					<span className={styles.cardRowLabel}>
						{advance.advanceStatus === 'CLOSED'
							? 'Closed'
							: advance.advanceStatus === 'WRITTEN_OFF'
								? 'Written Off'
								: advance.advanceStatus === 'FORECLOSED'
									? 'Foreclosed'
									: 'Rejected'}
					</span>
					<span className={styles.cardRowValue}>
						{advance.closedOn || advance.updatedOn}
						{advance.closureRemarks && ` • ${advance.closureRemarks}`}
					</span>
				</div>
			)}

			{/* Progress Bar (ACTIVE only) */}
			{advance.advanceStatus === 'ACTIVE' && (
				<div className={styles.progressSection}>
					<div className={styles.progressBar}>
						<div
							className={styles.progressFill}
							style={{ width: `${progressPercent}%` }}
						/>
					</div>
					<div className={styles.progressLabel}>
						₹{formatINR(advance.totalRepaid || 0)} of ₹
						{formatINR(advance.disbursedAmount || 0)}
					</div>
				</div>
			)}

			{/* Installments Toggle */}
			{advance.installments && advance.installments.length > 0 && (
				<button className={styles.toggleBtn} onClick={onToggleExpand}>
					<TrendingUp size={16} />
					<span>View Schedule</span>
					{isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
				</button>
			)}

			{/* Installment Table (Expanded) */}
			{isExpanded && advance.installments && advance.installments.length > 0 && (
				<InstallmentTable installments={advance.installments} />
			)}

			{/* Action Buttons */}
			<ActionButtonRow
				advance={advance}
				onReject={onReject}
				onApprove={onApprove}
				navigate={navigate}
			/>
		</div>
	)
}

/* ─────────────────────────────────────────────────────────────
   InstallmentTable Sub-Component
   ───────────────────────────────────────────────────────────── */
const InstallmentTable = ({ installments }) => {
	return (
		<div className={styles.installmentTable}>
			<div className={styles.installmentHeader}>
				<div className={styles.installmentCol1}>EMI</div>
				<div className={styles.installmentCol2}>Cycle</div>
				<div className={styles.installmentCol3}>Due</div>
				<div className={styles.installmentCol4}>Status</div>
			</div>

			{installments.map((inst, idx) => {
				const emiConfig = EMI_STATUS_CONFIG[inst.emiStatus] || {
					icon: '?',
					color: '#90A4AE',
				}

				return (
					<div key={inst.scheduleIdentifier || idx}>
						<div className={styles.installmentRow}>
							<div className={styles.installmentCol1}>{inst.emiSequence}</div>
							<div className={styles.installmentCol2}>
								{inst.cycleLabel || '—'}
							</div>
							<div className={styles.installmentCol3}>
								₹{formatINR(inst.dueAmount || 0)}
							</div>
							<div className={styles.installmentCol4}>
								<span
									className={styles.emiStatusIcon}
									style={{ color: emiConfig.color }}
									title={inst.emiStatus}
								>
									{emiConfig.icon}
								</span>
								<span className={styles.emiStatusLabel}>
									{inst.emiStatus}
								</span>
							</div>
						</div>

						{/* PARTIAL Shortfall Detail */}
						{inst.emiStatus === 'PARTIAL' && inst.shortfallAmount > 0 && (
							<div className={styles.shortfallRow}>
								<span className={styles.shortfallText}>
									₹{formatINR(inst.deductedAmount || 0)} deducted of ₹
									{formatINR(inst.dueAmount || 0)} due. Shortfall ₹
									{formatINR(inst.shortfallAmount || 0)} carries to EMI{' '}
									{inst.carryForwardToSequence}.
								</span>
							</div>
						)}
					</div>
				)
			})}
		</div>
	)
}

/* ─────────────────────────────────────────────────────────────
   ActionButtonRow Sub-Component
   ───────────────────────────────────────────────────────────── */
const ActionButtonRow = ({ advance, onReject, onApprove, navigate }) => {
	const getActions = () => {
		switch (advance.advanceStatus) {
			case 'PENDING_APPROVAL':
				return [
					{
						label: 'Approve',
						action: () => onApprove(advance),
						variant: 'success',
					},
					{
						label: 'Reject',
						action: () => onReject(advance),
						variant: 'danger',
					},
				]
			case 'ACTIVE':
				return [
					{
						label: 'Write Off',
						action: () =>
							navigate(`/payroll/advance/${advance.advanceIdentifier}/write-off`, { state: { advance } }),
						variant: 'warning',
					},
					{
						label: 'Foreclose',
						action: () =>
							navigate(`/payroll/advance/${advance.advanceIdentifier}/foreclose`, { state: { advance } }),
						variant: 'info',
					},
					{
						label: 'Reschedule',
						action: () =>
							navigate(`/payroll/advance/${advance.advanceIdentifier}/reschedule`, { state: { advance } }),
						variant: 'secondary',
					},
				]
			default:
				return []
		}
	}

	const actions = getActions()

	if (actions.length === 0) return null

	return (
		<div className={styles.actionButtonRow}>
			{actions.map((action, idx) => (
				<button
					key={idx}
					className={`${styles.actionBtn} ${
						styles[`actionBtn${action.variant.charAt(0).toUpperCase() + action.variant.slice(1)}`]
					}`}
					onClick={action.action}
				>
					{action.label}
				</button>
			))}
		</div>
	)
}

export default StaffAdvanceLedgerScreen